import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { FileUp, Trash2, Table2, Lock, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrutalButton, BentoCard } from "@/components/dashboard/BentoCard";

const FIELD_LABELS = {
  owner: "Owner",
  parcel_number: "Parcel #",
  address: "Address",
  city: "City",
  amount_owed: "Amount Owed",
  sale_date: "Sale Date",
  status: "Status",
  property_type: "Property Type",
  acres: "Acres",
  instrument_number: "Instrument / Case #",
};

export default function ImportsPage() {
  const { isSubscribed } = useAuth();
  return (
    <div className="space-y-8" data-testid="imports-page">
      <div>
        <div className="data-label">Custom Data</div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">My Imports</h1>
        <p className="text-zinc-600 mt-2 max-w-2xl text-sm">
          Upload any county's delinquent or sale list as a CSV. Lien Scout parses it,
          scores every row with the AI Deal Score, and makes it searchable in your
          Properties table — visible only to you.
        </p>
      </div>
      {isSubscribed ? (
        <>
          <UploadFlow />
          <UploadsList />
        </>
      ) : (
        <BentoCard>
          <div className="flex items-center gap-3 p-2" data-testid="imports-locked">
            <Lock size={18} className="text-red-600" />
            <span className="text-sm">
              Custom CSV imports are a subscriber feature.{" "}
              <Link to="/pricing" className="text-red-600 font-semibold hover:underline">
                Start your 3-day free trial
              </Link>{" "}
              to unlock.
            </span>
          </div>
        </BentoCard>
      )}
    </div>
  );
}

function UploadFlow() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [meta, setMeta] = useState({ name: "", state: "", jurisdiction: "", city: "" });
  const [result, setResult] = useState(null);

  const previewMut = useMutation({
    mutationFn: async (f) => {
      const fd = new FormData();
      fd.append("file", f);
      return (await api.post("/imports/preview", fd)).data;
    },
    onSuccess: (data) => {
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setResult(null);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not read that file");
      setFile(null);
    },
  });

  const importMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", meta.name);
      fd.append("state", meta.state);
      fd.append("jurisdiction", meta.jurisdiction);
      fd.append("city", meta.city);
      fd.append("mapping", JSON.stringify(mapping));
      return (await api.post("/imports", fd)).data;
    },
    onSuccess: (data) => {
      toast.success(`Imported ${data.imported} properties`);
      setResult(data);
      setFile(null);
      setPreview(null);
      setMapping({});
      setMeta({ name: "", state: "", jurisdiction: "", city: "" });
      queryClient.invalidateQueries({ queryKey: ["imports"] });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed");
    },
  });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    previewMut.mutate(f);
  };

  const canImport = preview && meta.name.trim() && /^[A-Za-z]{2}$/.test(meta.state.trim()) &&
    ["owner", "parcel_number", "address"].some((k) => mapping[k]);

  return (
    <BentoCard>
      <div className="p-2 space-y-6">
        <div className="flex items-center gap-2">
          <Upload size={16} className="text-red-600" />
          <h2 className="font-display font-bold text-lg">Upload a CSV</h2>
        </div>

        {result ? (
          <div className="border-2 border-black bg-yellow-50 p-4 space-y-2" data-testid="import-success">
            <div className="flex items-center gap-2 font-display font-bold">
              <CheckCircle2 size={16} className="text-green-700" />
              {result.imported} properties imported
              {result.skipped ? ` · ${result.skipped} rows skipped` : ""}
              {result.truncated ? ` · ${result.truncated} rows over the 5,000 cap` : ""}
            </div>
            <Link
              to={`/properties?source_id=${result.upload.source_id}`}
              data-testid="import-view-properties-link"
              className="inline-flex items-center gap-1 text-red-600 font-semibold text-sm hover:underline"
            >
              <Table2 size={14} /> View them in Properties →
            </Link>
          </div>
        ) : null}

        {!preview ? (
          <label
            className="block border-2 border-dashed border-black bg-zinc-50 hover:bg-yellow-50 p-10 text-center cursor-pointer transition-colors"
            data-testid="imports-dropzone"
          >
            <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={onFile} data-testid="imports-file-input" />
            <FileUp size={28} className="mx-auto text-red-600" />
            <div className="font-display font-bold mt-3">
              {previewMut.isPending ? "Reading file…" : "Choose a CSV or Excel file"}
            </div>
            <div className="text-xs text-zinc-500 mt-1 font-mono-data">
              .csv or .xlsx · max 5 MB · up to 5,000 rows · headers required
            </div>
          </label>
        ) : (
          <div className="space-y-6" data-testid="imports-mapping-step">
            <div className="font-mono-data text-xs text-zinc-500">
              {file?.name} · {preview.total_rows.toLocaleString()} rows detected
              {preview.total_rows > preview.max_rows ? ` (first ${preview.max_rows.toLocaleString()} will import)` : ""}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetaInput label="Import name *" value={meta.name} testid="import-name-input"
                placeholder="e.g. Knox County 2026 Sale"
                onChange={(v) => setMeta({ ...meta, name: v })} />
              <MetaInput label="State (2-letter) *" value={meta.state} testid="import-state-input"
                placeholder="TN" maxLength={2}
                onChange={(v) => setMeta({ ...meta, state: v.toUpperCase() })} />
              <MetaInput label="County / Jurisdiction" value={meta.jurisdiction} testid="import-jurisdiction-input"
                placeholder="Knox County"
                onChange={(v) => setMeta({ ...meta, jurisdiction: v })} />
              <MetaInput label="Default city" value={meta.city} testid="import-city-input"
                placeholder="Knoxville"
                onChange={(v) => setMeta({ ...meta, city: v })} />
            </div>

            <div>
              <div className="data-label mb-2">Map your columns (Owner, Parcel # or Address required)</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(preview.target_fields || Object.keys(FIELD_LABELS)).map((field) => (
                  <div key={field} className="flex items-center gap-2">
                    <span className="font-mono-data text-[11px] uppercase w-28 shrink-0 text-zinc-600">
                      {FIELD_LABELS[field] || field}
                    </span>
                    <select
                      data-testid={`mapping-select-${field}`}
                      className="flex-1 border-2 border-black bg-white px-2 py-1.5 text-sm"
                      value={mapping[field] || ""}
                      onChange={(e) => setMapping({ ...mapping, [field]: e.target.value })}
                    >
                      <option value="">— ignore —</option>
                      {preview.headers.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <SamplePreview headers={preview.headers} rows={preview.sample_rows} />

            <div className="flex items-center gap-3">
              <BrutalButton
                data-testid="import-submit-btn"
                onClick={() => importMut.mutate()}
                disabled={!canImport || importMut.isPending}
              >
                <Upload size={14} /> {importMut.isPending ? "Importing…" : "Import properties"}
              </BrutalButton>
              <button
                data-testid="import-cancel-btn"
                className="text-sm text-zinc-500 hover:text-black underline"
                onClick={() => { setFile(null); setPreview(null); setMapping({}); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </BentoCard>
  );
}

function MetaInput({ label, value, onChange, placeholder, maxLength, testid }) {
  return (
    <div>
      <div className="data-label mb-1">{label}</div>
      <input
        data-testid={testid}
        className="w-full border-2 border-black px-3 py-2 text-sm bg-white"
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function SamplePreview({ headers, rows }) {
  if (!rows?.length) return null;
  return (
    <div className="overflow-x-auto border-2 border-black">
      <table className="w-full text-xs">
        <thead className="bg-brand-black text-white">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-3 py-2 text-left font-mono-data text-[10px] uppercase whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-zinc-200">
              {headers.map((h) => (
                <td key={h} className="px-3 py-1.5 whitespace-nowrap max-w-[220px] truncate">{r[h]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UploadsList() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: uploads = [], isLoading } = useQuery({
    queryKey: ["imports"],
    queryFn: async () => (await api.get("/imports")).data,
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/imports/${id}`)).data,
    onSuccess: (data) => {
      toast.success(`Deleted import (${data.properties_removed} properties removed)`);
      queryClient.invalidateQueries({ queryKey: ["imports"] });
    },
    onError: () => toast.error("Could not delete import"),
  });

  if (isLoading || !uploads.length) {
    return (
      <div className="text-sm text-zinc-500 font-mono-data" data-testid="imports-empty">
        {isLoading ? "Loading imports…" : "No imports yet — your uploaded lists will appear here."}
      </div>
    );
  }

  return (
    <BentoCard>
      <div className="p-2">
        <h2 className="font-display font-bold text-lg mb-4">Your imports</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-black text-left">
                <th className="py-2 pr-4 font-mono-data text-[10px] uppercase">Name</th>
                <th className="py-2 pr-4 font-mono-data text-[10px] uppercase">State</th>
                <th className="py-2 pr-4 font-mono-data text-[10px] uppercase">Rows</th>
                <th className="py-2 pr-4 font-mono-data text-[10px] uppercase">Uploaded</th>
                <th className="py-2 font-mono-data text-[10px] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {uploads.map((u) => (
                <tr key={u.id} className="border-b border-zinc-200" data-testid={`import-row-${u.id}`}>
                  <td className="py-2.5 pr-4 font-semibold">{u.name}</td>
                  <td className="py-2.5 pr-4 font-mono-data">{u.state}</td>
                  <td className="py-2.5 pr-4 font-mono-data">{u.row_count}</td>
                  <td className="py-2.5 pr-4 font-mono-data text-xs">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="py-2.5">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`import-view-${u.id}`}
                        onClick={() => navigate(`/properties?source_id=${u.source_id}`)}
                        className="inline-flex items-center gap-1 border-2 border-black px-2 py-1 text-xs font-semibold hover:bg-yellow-50"
                      >
                        <Table2 size={12} /> View
                      </button>
                      <button
                        data-testid={`import-delete-${u.id}`}
                        onClick={() => {
                          if (window.confirm(`Delete "${u.name}" and its ${u.row_count} properties? This cannot be undone.`)) {
                            deleteMut.mutate(u.id);
                          }
                        }}
                        disabled={deleteMut.isPending}
                        className="inline-flex items-center gap-1 border-2 border-black px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </BentoCard>
  );
}
